/**
 * Phase 482 wave 3 task 3.1 (card #1779, AC-5) — reading a unified diff.
 *
 * The console's `write_confirm` card can carry an ENGINE-COMPUTED preview of the write it
 * gates: `{ kind: "unified_diff", text, meta }`, built by the connector's
 * `IProposalPreviewProvider` and persisted verbatim on the card by
 * `SendChatMessageTool.BuildConsoleCardPayload`. `unified_diff` is a generic preview kind: the
 * text is a line-based unified diff — `--- <from>` / `+++ <to>` headers, `@@` hunk headers,
 * then one line per source line prefixed `+` (added), `-` (removed) or ` ` (unchanged), every
 * line ending in `\n`. This module is the panel's half of that contract: it turns the text into
 * lines a renderer can colour, and nothing else.
 *
 * <b>Why the parse lives here and not in the design system.</b> `DiffBlock` renders lines that
 * are already classified. The classification is a reading of a WIRE FORMAT — the format the
 * engine emits — so it belongs beside the wire types (`types.ts`), where the one reading of it
 * lives. A design-system primitive that sniffed diff syntax would be a second parser drifting
 * away from the producer nobody made it read.
 *
 * <b>Both functions are pure and dependency-free</b> (the `chatRoutes.ts` discipline), so the
 * node-only console test tree can exercise them directly rather than mirror their rules.
 */

/** The five kinds of line a unified diff is made of — the union `DiffBlock` paints from. */
export type DiffLineClass = "header" | "hunk" | "add" | "del" | "ctx";

/** The one preview kind this module knows how to read: the `kind` string an engine preview
 *  provider sets when its text is a unified diff in the format described above. A card whose
 *  `preview.kind` is anything else is left unrendered rather than mis-rendered — a future kind
 *  is a new renderer's job, not this one's guess. */
export const DIFF_PREVIEW_KIND = "unified_diff";

/**
 * Split a unified diff into its lines.
 *
 * `\r\n` is normalised (a YAML source round-tripped through a Windows editor would otherwise
 * leave a stray `\r` at the end of every rendered line), and the ONE empty string produced by
 * the diff's terminating newline is dropped — the format ends every line, including the
 * last, with `\n`, so keeping it would render a blank row under every diff. An empty or
 * whitespace-only diff (the engine emits `""` for "no change") yields no lines at all.
 */
export function splitDiff(text: string): string[] {
  if (text.trim() === "") return [];
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  // Exactly one trailing empty: a diff that genuinely ends in a blank line emits `"\n\n"`,
  // whose split gives ["…", "", ""] — the blank line survives, the terminator goes.
  if (lines.length > 0 && lines[lines.length - 1] === "") lines.pop();
  return lines;
}

/**
 * Classify ONE line of a unified diff, from that line alone.
 *
 * The rules, in the order they are applied — the order is the whole subtlety:
 *
 *   1. `--- <label>` / `+++ <label>` are the file HEADERS. The trailing SPACE is load-bearing:
 *      it is what separates the header `--- sales-chaser@0.1.1` from the deletion of a line
 *      whose own text begins with dashes (a markdown rule `---` inside a persona prompt
 *      arrives here as `----`, four characters, no space). Matching on the prefix alone would
 *      paint that deletion as a header — grey, unremarkable, and wrong about the one thing the
 *      reader is being asked to approve.
 *   2. `@@ …` is a HUNK header — where in the file the next block sits.
 *   3. A leading `+` or `-` is an addition or a deletion.
 *   4. Everything else is context: a ` `-prefixed unchanged line, the `\ No newline at end of
 *      file` marker (the standard unified-diff note ABOUT the file, never an edit to it, so it
 *      must not wear an edit's colour), and the empty string.
 *
 * Line-local by design: a classifier that tracked "am I inside a hunk yet" would be a state
 * machine the caller has to drive in the right order, and the rule above already resolves the
 * only ambiguity that state would have bought.
 */
export function classifyDiffLine(line: string): DiffLineClass {
  if (line.startsWith("--- ") || line.startsWith("+++ ")) return "header";
  if (line.startsWith("@@")) return "hunk";
  if (line.startsWith("+")) return "add";
  if (line.startsWith("-")) return "del";
  return "ctx";
}
